import { useState, useEffect, useMemo } from 'react';
import { Modal, Select, Button, Spin, message } from 'antd';
import { getAllUsersAndAccounts, impersonateUser } from '../../services/users';
import type { Account, User } from 'src/types/user';

const { Option } = Select;

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ImpersonateModal({ open, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>();
  const [selectedUser, setSelectedUser] = useState<string>();
  const [impersonating, setImpersonating] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoading(true);
    getAllUsersAndAccounts(true)
      .then((data) => {
        setAccounts(data.accounts);
        setUsers(data.users);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  const filteredUsers = useMemo(() => {
    if (!selectedAccount) {
      return users.filter((u) => u.role !== 'superadmin');
    }
    return users.filter(
      (u) =>
        u.role !== 'superadmin' && u.accountsAccess.some((a) => a.accountId === selectedAccount),
    );
  }, [users, selectedAccount]);

  const filteredAccounts = useMemo(() => {
    if (!selectedUser) {
      return accounts;
    }
    const user = users.find((u) => u.email === selectedUser);
    if (!user) {
      return [];
    }
    const allowed = new Set(user.accountsAccess.map((a) => a.accountId));
    return accounts.filter((a) => allowed.has(a.accountId));
  }, [accounts, users, selectedUser]);

  useEffect(() => {
    if (!selectedUser || !selectedAccount) {
      return;
    }
    const stillValid = filteredAccounts.some((a) => a.accountId === selectedAccount);
    if (!stillValid) {
      setSelectedAccount(undefined);
    }
  }, [selectedUser, filteredAccounts, selectedAccount]);

  useEffect(() => {
    if (!selectedUser || !selectedAccount) {
      return;
    }
    const stillValid = filteredUsers.some((u) => u.email === selectedUser);
    if (!stillValid) {
      setSelectedUser(undefined);
    }
  }, [selectedAccount, filteredUsers, selectedUser]);

  const handleImpersonate = async () => {
    if (!selectedAccount || !selectedUser) {
      return;
    }
    setImpersonating(true);
    try {
      const { token } = await impersonateUser(selectedUser, selectedAccount);
      localStorage.setItem('overrideAccessToken', token);
      messageApi.success('Impersonation token stored');
      onClose();
      window.location.reload();
    } catch {
      messageApi.error('Failed to impersonate');
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <Modal title='Impersonate User' open={open} onCancel={onClose} footer={null} destroyOnClose>
      {contextHolder}
      {loading ? (
        <Spin tip='Loading data...' />
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Select
              showSearch
              allowClear
              placeholder='Select User'
              style={{ width: '100%' }}
              value={selectedUser}
              onChange={setSelectedUser}
              optionFilterProp='children'
            >
              {filteredUsers.map((u) => (
                <Option key={u.email} value={u.email}>
                  {u.firstName} {u.lastName} ({u.email}) [{u.role}]
                </Option>
              ))}
            </Select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Select
              showSearch
              allowClear
              placeholder='Select Account'
              style={{ width: '100%' }}
              value={selectedAccount}
              onChange={setSelectedAccount}
              optionFilterProp='children'
            >
              {filteredAccounts.map((a) => (
                <Option key={a.accountId} value={a.accountId}>
                  {a.accountName} ({a.accountType})
                </Option>
              ))}
            </Select>
          </div>
          <Button
            type='primary'
            block
            disabled={!selectedAccount || !selectedUser}
            loading={impersonating}
            onClick={handleImpersonate}
          >
            Impersonate
          </Button>
        </>
      )}
    </Modal>
  );
}
